import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useWatchAuth } from '@/hooks/useWatchAuth'

export default function WatchLogin() {
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)
  const { login, checkAuth, isAuthenticated } = useWatchAuth()
  const navigate = useNavigate()

  useEffect(() => {
    checkAuth()
  }, [checkAuth])

  useEffect(() => {
    if (isAuthenticated) {
      navigate(window.location.hostname.startsWith('watch.') ? '/' : '/watch')
    }
  }, [isAuthenticated, navigate])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setLoading(true)

    try {
      await login(username, password)
      navigate(window.location.hostname.startsWith('watch.') ? '/' : '/watch')
    } catch (err) {
      if (err instanceof Error && err.message.includes('Web player not enabled')) {
        setError('Web player access is not enabled for this account')
      } else {
        setError('Invalid username or password')
      }
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="gs min-h-screen flex items-center justify-center relative overflow-hidden px-4">
      {/* Ambient background art */}
      <div className="gs-art-stadium gs-grain absolute inset-0" />
      <div
        className="absolute inset-0"
        style={{ background: 'radial-gradient(ellipse at center, transparent, oklch(15% 0.012 250 / 0.85))' }}
      />

      {/* Accent glow blob */}
      <div
        className="absolute top-[-200px] right-[-100px] w-[600px] h-[600px] rounded-full opacity-30 blur-[60px]"
        style={{ background: 'radial-gradient(circle, var(--gs-accent-glow), transparent 60%)' }}
      />

      {/* Wordmark top-left */}
      <div className="absolute top-8 left-8 flex items-baseline z-10">
        <span className="text-[22px] font-extrabold tracking-tight" style={{ color: 'var(--gs-ink-0)' }}>OBA</span>
        <span
          className="gs-mono text-[11px] font-semibold uppercase ml-1.5"
          style={{ letterSpacing: '0.18em', color: 'var(--gs-accent)' }}
        >
          stream
        </span>
        <span className="gs-mono text-[9px] font-semibold ml-1.5" style={{ color: 'var(--gs-ink-3)' }}>v2</span>
      </div>

      {/* Login card */}
      <div className="gs-glass-strong gs-grain relative z-10 w-full max-w-[520px] p-11 rounded-3xl">
        <div
          className="gs-mono text-[10px] font-semibold mb-3.5"
          style={{ letterSpacing: '0.18em', color: 'var(--gs-accent)' }}
        >
          SIGN IN TO CONTINUE
        </div>
        <h1
          className="text-4xl font-bold mb-2"
          style={{ letterSpacing: '-0.025em', lineHeight: 1.05, color: 'var(--gs-ink-0)' }}
        >
          Welcome back.
        </h1>
        <p className="text-sm mb-7" style={{ color: 'var(--gs-ink-2)', lineHeight: 1.5 }}>
          Sign in with your OBAStream credentials to start watching.
        </p>

        <form className="space-y-4" onSubmit={handleSubmit}>
          {error && (
            <div
              className="px-4 py-3 rounded-xl text-sm"
              style={{
                background: 'oklch(70% 0.20 25 / 0.12)',
                border: '1px solid oklch(70% 0.20 25 / 0.3)',
                color: 'oklch(82% 0.18 25)',
              }}
            >
              {error}
            </div>
          )}

          <div>
            <label
              className="gs-mono block mb-2 text-[10px] font-medium"
              style={{ letterSpacing: '0.14em', color: 'var(--gs-ink-2)' }}
              htmlFor="username"
            >
              USERNAME
            </label>
            <input
              id="username"
              name="username"
              type="text"
              required
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              className="w-full h-12 px-4 rounded-xl text-sm focus:outline-none transition-all duration-200"
              style={{
                background: 'oklch(82% 0.02 250 / 0.05)',
                border: '1px solid var(--gs-glass-stroke)',
                color: 'var(--gs-ink-0)',
                fontFamily: 'inherit',
              }}
              placeholder="Enter your username"
            />
          </div>

          <div>
            <label
              className="gs-mono block mb-2 text-[10px] font-medium"
              style={{ letterSpacing: '0.14em', color: 'var(--gs-ink-2)' }}
              htmlFor="password"
            >
              PASSWORD
            </label>
            <input
              id="password"
              name="password"
              type="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full h-12 px-4 rounded-xl text-sm focus:outline-none transition-all duration-200"
              style={{
                background: 'oklch(82% 0.02 250 / 0.05)',
                border: '1px solid var(--gs-glass-stroke)',
                color: 'var(--gs-ink-0)',
                fontFamily: 'inherit',
              }}
              placeholder="Enter your password"
            />
          </div>

          {/* Divider */}
          <div className="flex items-center gap-3 py-2">
            <div className="flex-1 h-px" style={{ background: 'var(--gs-glass-stroke)' }} />
          </div>

          <button
            type="submit"
            disabled={loading}
            className="gs-focus-ring w-full h-12 rounded-full text-sm font-semibold transition-all duration-200 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
            style={{
              background: 'var(--gs-ink-0)',
              color: 'var(--gs-bg-0)',
              border: 'none',
              fontFamily: 'inherit',
            }}
          >
            {loading ? 'Signing in...' : 'Sign In'}
          </button>

          <button
            type="button"
            className="w-full h-12 rounded-full text-sm font-medium cursor-pointer transition-all duration-200"
            style={{
              background: 'transparent',
              color: 'var(--gs-ink-1)',
              border: '1px solid var(--gs-glass-stroke-strong)',
              fontFamily: 'inherit',
            }}
          >
            Use M3U / Xtream
          </button>
        </form>
      </div>

      {/* Bottom helper */}
      <div className="absolute bottom-8 left-8 right-8 flex justify-between items-center z-10">
        <span
          className="gs-mono text-[10px]"
          style={{ letterSpacing: '0.12em', color: 'var(--gs-ink-3)' }}
        >
          OBASTREAM V2 · WEB PLAYER
        </span>
        <span
          className="gs-mono text-[10px]"
          style={{ letterSpacing: '0.12em', color: 'var(--gs-ink-3)' }}
        >
          CONTACT ADMIN FOR ACCESS
        </span>
      </div>
    </div>
  )
}
